import * as Sentry from "@sentry/react";
import { createRoot } from "react-dom/client";
import App from "./App.tsx";
import "./index.css";
import { installPopStateGuard } from "./lib/navigation/popStateGuard";
import { redactReplayRecordingEvent, redactSentryEvent } from "./lib/sentry/redactEvent";
import { runReplayOnlyOnCleanUrls } from "./lib/sentry/replayGate";
import { debugLog } from "./utils/debugLog";
import { logger } from "./utils/logger";
import { repairBrokenAutomationBindings } from "./utils/foreignScriptError";

// Automation browsers (BetterWright/CloakBrowser) can leave injected
// playwright-style binding shims whose dispatch global is missing, making
// plain web APIs (observed: new WebSocket) throw on every call and crash the
// app into the root error boundary. Repair them before anything else runs.
// No-op outside automation environments.
repairBrokenAutomationBindings();

// Initialize Sentry
// VITE_SENTRY_ENABLED defaults to true so deployed builds get Sentry without extra config.
// Local .env sets VITE_SENTRY_ENABLED=false to silence Sentry during dev.
const sentryDsn = import.meta.env.VITE_SENTRY_DSN;
const sentryEnv = import.meta.env.VITE_SENTRY_ENV || "development";
const sentryEnabled = import.meta.env.VITE_SENTRY_ENABLED !== "false";

if (sentryEnabled && sentryDsn) {
  const client = Sentry.init({
    dsn: sentryDsn,
    environment: sentryEnv,
    integrations: [Sentry.browserTracingIntegration()],
    // Performance Monitoring
    tracesSampleRate: sentryEnv === "production" ? 0.1 : 1.0,
    // Session Replay — the integration is attached below, not here.
    //
    // The Sentry Team plan reserves 50 replays/month for the whole org, and 10%
    // blanket session sampling burned 40 of them in the first two days of the
    // 2026-07-19 billing period, so the replays attached to real errors were
    // being dropped along with everything else. Record only errored sessions:
    // with a session rate of 0 and an error rate of 1.0 the SDK runs in buffer
    // mode, so an error still uploads the 60s before it plus everything after.
    // Raise this above 0 only if the reserved replay quota is raised first.
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 1.0,
  });
  // Auth tokens arrive in the URL hash, query and path, and Sentry copies location.href onto
  // every event — never let one reach Sentry. See redactSentryEvent for why not beforeSend.
  client?.addEventProcessor(redactSentryEvent);
  // Recording starts the moment the integration is added, and its rrweb Meta frame carries
  // location.href past every hook — so hold it back until the URL is credential-free. On the
  // ordinary page load, whose URL is already clean, this attaches synchronously right here.
  if (client) {
    runReplayOnlyOnCleanUrls(
      () =>
        client.addIntegration(
          // The recording payload bypasses every event hook — redact what its custom frames carry.
          Sentry.replayIntegration({ beforeAddRecordingEvent: redactReplayRecordingEvent })
        ),
      // That Meta frame rides every checkout, not just the first, so a recording that started
      // clean still has to be torn down when the app navigates ONTO a token URL.
      () => void Sentry.getReplay()?.stop()
    );
  }
  debugLog("✅ Sentry initialized", { environment: sentryEnv });
} else {
  debugLog("⚠️ Sentry skipped", {
    enabled: sentryEnabled,
    dsnSet: Boolean(sentryDsn),
  });
}

debugLog("🚀 main.tsx: Starting application");
debugLog("🌍 Environment check:", {
  VITE_SUPABASE_URL: import.meta.env.VITE_SUPABASE_URL ? "SET" : "MISSING",
  VITE_SUPABASE_ANON_KEY: import.meta.env.VITE_SUPABASE_ANON_KEY ? "SET" : "MISSING",
  NODE_ENV: import.meta.env.NODE_ENV,
});

// Suppress browser PWA install prompt ("Install Franklin" tooltip)
window.addEventListener("beforeinstallprompt", (e) => {
  e.preventDefault();
});

// Before the router mounts, so a page can hold a Back/Forward step (the Brief page).
installPopStateGuard();

const rootElement = document.getElementById("root");
debugLog("📦 Root element found:", !!rootElement);

if (!rootElement) {
  logger.error("ui", "root-missing", "No root element found in DOM");
  document.body.innerHTML =
    '<div style="padding: 20px; color: red;">Error: Root element not found</div>';
} else {
  try {
    debugLog("🎯 Creating React root...");
    const root = createRoot(rootElement);
    debugLog("🎯 Rendering App component...");
    root.render(<App />);
    debugLog("✅ App rendered successfully");
  } catch (error) {
    logger.error("ui", "render-error", "Error during React rendering", error as Error);
    rootElement.innerHTML = `<div style="padding: 20px; color: red;">Error: ${(error as Error).message}</div>`;
  }
}
